import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useUpdatePost } from '@/hooks/usePosts';
import { useToast } from '@/hooks/use-toast';
import { Briefcase, MessageSquare, Megaphone, Loader2 } from 'lucide-react';
import type { Database } from '@/integrations/supabase/types';

type PostCategory = Database['public']['Enums']['post_category'];

const CATEGORIES: { value: PostCategory; label: string; icon: React.ElementType }[] = [
  { value: 'referral', label: 'Opportunity', icon: Briefcase },
  { value: 'discussion', label: 'Discussion', icon: MessageSquare },
  { value: 'plug', label: 'Shameless Plug', icon: Megaphone },
];

interface EditPostDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  postId: string;
  initialContent: string;
  initialCategory: PostCategory;
}

export function EditPostDialog({
  open,
  onOpenChange,
  postId,
  initialContent,
  initialCategory,
}: EditPostDialogProps) {
  const [content, setContent] = useState(initialContent);
  const [category, setCategory] = useState<PostCategory>(initialCategory);
  const updatePost = useUpdatePost();
  const { toast } = useToast();

  const handleSave = async () => {
    if (!content.trim()) return;

    try {
      await updatePost.mutateAsync({ id: postId, content: content.trim(), category });
      toast({ title: 'Post updated' });
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Error updating post',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Post</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="min-h-[120px] resize-none"
          />
          <Select value={category} onValueChange={(v) => setCategory(v as PostCategory)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  <div className="flex items-center gap-2">
                    <cat.icon className="h-4 w-4" />
                    <span>{cat.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!content.trim() || updatePost.isPending}
              className="btn-qbn-primary"
            >
              {updatePost.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
